export const TEAM_FAVORITES_STORAGE_KEY =
  'volleywire.favoriteTeams.v1';

export const TEAM_FAVORITES_EVENT =
  'volleywire:favorites-changed';

export const CONFERENCE_FAVORITES_STORAGE_KEY =
  'volleywire.favoriteConferences.v1';

export const CONFERENCE_FAVORITES_EVENT =
  'volleywire:conference-favorites-changed';

const CONFERENCE_ALIASES = {
  'american-athletic': 'american',
  'american-athletic-conference': 'american',
  'atlantic-coast': 'acc',
  'atlantic-coast-conference': 'acc',
  'conference-usa': 'cusa',
  'mid-american': 'mac',
  'mid-american-conference': 'mac',
  'missouri-valley': 'mvc',
  'missouri-valley-conference': 'mvc',
  'ohio-valley': 'ovc',
  'ohio-valley-conference': 'ovc',
  southeastern: 'sec',
  'southeastern-conference': 'sec',
  southern: 'socon',
  'southern-conference': 'socon',
  'west-coast': 'wcc',
  'west-coast-conference': 'wcc',
  'western-athletic': 'uac',
  'western-athletic-conference': 'uac',
  'wac': 'uac',
  'united-athletic': 'uac',
  'united-athletic-conference': 'uac',
};

function readList(storageKey) {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored =
      window.localStorage.getItem(
        storageKey
      );

    const parsed =
      stored
        ? JSON.parse(stored)
        : [];

    if (!Array.isArray(parsed)) {
      return [];
    }

    return Array.from(
      new Set(
        parsed
          .filter(
            (value) =>
              typeof value ===
              'string'
          )
          .map((value) =>
            value.trim()
          )
          .filter(Boolean)
      )
    );
  } catch {
    return [];
  }
}

function saveList({
  storageKey,
  eventName,
  detailKey,
  values,
}) {
  const normalized =
    Array.from(
      new Set(
        values
          .map((value) =>
            String(value || '')
              .trim()
          )
          .filter(Boolean)
      )
    );

  if (typeof window === 'undefined') {
    return normalized;
  }

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify(normalized)
    );

    window.dispatchEvent(
      new CustomEvent(
        eventName,
        {
          detail: {
            [detailKey]:
              normalized,
          },
        }
      )
    );
  } catch {
    // Keep favorites usable in memory
    // when browser storage is blocked.
  }

  return normalized;
}

export function normalizeTeamKey(
  value
) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');
}

export function getTeamFavoriteKey(
  team
) {
  const key = canonicalTeamSlug(
    team?.slug || team?.name || team
  );

  return key
    ? `team:${key}`
    : '';
}

export function getTeamFavoriteKeys(
  team
) {
  const values = [
    team?.slug,
    team?.name,
    team?.longName,
    ...(team?.aliases || []),
  ].filter(Boolean);
  const keys = values.flatMap((value) => {
    const canonical = getTeamFavoriteKey(value);
    const legacy = normalizeTeamKey(value);
    return [canonical, legacy ? `name:${legacy}` : ''];
  });
  return Array.from(new Set(keys.filter(Boolean)));
}

export function readTeamFavorites() {
  return readList(
    TEAM_FAVORITES_STORAGE_KEY
  );
}

export function saveTeamFavorites(
  favorites
) {
  return saveList({
    storageKey:
      TEAM_FAVORITES_STORAGE_KEY,
    eventName:
      TEAM_FAVORITES_EVENT,
    detailKey: 'favorites',
    values: favorites,
  });
}

export function isTeamFavorite(
  team,
  favoriteSet
) {
  return getTeamFavoriteKeys(
    team
  ).some((key) =>
    favoriteSet.has(key)
  );
}

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function normalizeConferenceKey(
  value
) {
  const slug = slugify(normalizeConference(value));

  if (!slug || ['unassigned', 'unknown', 'unaffiliated'].includes(slug)) {
    return '';
  }

  const withoutSuffix =
    slug.replace(
      /-(conference|league)$/,
      ''
    );

  return (
    CONFERENCE_ALIASES[slug] ||
    CONFERENCE_ALIASES[
      withoutSuffix
    ] ||
    withoutSuffix
  );
}

export function getConferenceFavoriteKey(
  conference
) {
  const values =
    typeof conference ===
    'string'
      ? [conference]
      : [
          conference?.slug,
          conference?.name,
          conference?.conferenceSlug,
          conference?.conference,
        ];

  for (const value of values) {
    const key =
      normalizeConferenceKey(
        value
      );

    if (key) {
      return key;
    }
  }

  return '';
}

export function getTeamConferenceKey(
  team
) {
  return getConferenceFavoriteKey({
    slug:
      team?.conferenceSlug,
    name:
      team?.conference,
  });
}

export function readConferenceFavorites() {
  return readList(
    CONFERENCE_FAVORITES_STORAGE_KEY
  )
    .map(normalizeConferenceKey)
    .filter(Boolean);
}

export function saveConferenceFavorites(
  favorites
) {
  return saveList({
    storageKey:
      CONFERENCE_FAVORITES_STORAGE_KEY,
    eventName:
      CONFERENCE_FAVORITES_EVENT,
    detailKey:
      'conferenceFavorites',
    values:
      favorites.map(
        normalizeConferenceKey
      ),
  });
}

export function isConferenceFavorite(
  conference,
  favoriteSet
) {
  const key =
    getConferenceFavoriteKey(
      conference
    );

  return Boolean(
    key &&
    favoriteSet.has(key)
  );
}
import {
  canonicalTeamSlug,
  normalizeConference,
} from '../data/teamIdentity';
